<template>
  <div class="add-dish-form-container slide-up-form" :class="{ show: show, hiding: isHiding }">
    <form id="add-dish-form" @submit.prevent="handleForm">
      <div>
        <p>Add Exposure Log</p>
        <button type="button" @click="hideForm" class="button secondary" id="close_form_button">
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
      </div>

      <div class="form-group">
        <label for="exposure-date-input">Date</label>
        <div class="date-input-wrapper">
          <input id="exposure-date-input" v-model="exposure_date" type="date" required />
          <span class="calendar-icon">
            <font-awesome-icon :icon="['fas', 'calendar-alt']" />
          </span>
        </div>
        <small class="input-hint">Tap to select a date</small>
      </div>

      <label for="anxiety_level">Anxiety Level (1-10)</label>
      <div class="slider-container">
        <span class="slider-value">{{ anxiety_level }}</span>
        <input
          id="anxiety_level"
          type="range"
          min="1"
          max="10"
          v-model.number="anxiety_level"
        />
      </div>

      <label>Taste</label>
      <div class="taste-buttons">
        <button
          type="button"
          :class="{ selected: taste === 'Good' }"
          @click="taste = 'Good'"
        >Good</button>
        <button
          type="button"
          :class="{ selected: taste === 'Okay' }"
          @click="taste = 'Okay'"
        >Okay</button>
        <button
          type="button"
          :class="{ selected: taste === 'Bad' }"
          @click="taste = 'Bad'"
        >Bad</button>
      </div>

      <label for="notes">Bodys Reaction</label>
      <textarea
        id="bodys_reaction_notes"
        v-model="bodys_reaction"
        placeholder="How did your body react to the exposure?"
        maxlength="500"
      ></textarea>

      <label for="notes">Additional Notes</label>
      <textarea
        id="additional_notes"
        v-model="additional_notes"
        placeholder="Do you have any additional thoughts?"
        maxlength="500"
      ></textarea>

      <button class="button primary" type="submit">
        <span v-if="!loading">Add Log</span>
        <span v-if="loading" class="loader button-loader"></span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { fetchWithAuth } from '@/utils/api';
import { type ExposureLog } from '@/types';

const props = defineProps<{ show: boolean, dishId: number }>();
const emit = defineEmits<{
  (e: 'hideForm'): void;
  (e: 'logAdded', newLog: any): void;
}>();

const store = useStore();
const apiUrl = computed(() => store.getters.getApiUrl);

const exposure_date = ref('');
const anxiety_level = ref(5);
const taste = ref(''); // "Good", "Okay", or "Bad"
const bodys_reaction = ref('');
const additional_notes = ref('');
const loading = ref(false);

const hideForm = () => {
  emit('hideForm');
};

const resetForm = () => {
  exposure_date.value = '';
  anxiety_level.value = 5;
  taste.value = '';
  bodys_reaction.value = '';
  additional_notes.value = '';
};

const handleForm = async () => {
  if (!exposure_date.value) {
    alert('Please select a date.');
    return;
  }
  if (!taste.value) {
    alert('Please select a taste option.');
    return;
  }

  const new_log_data: ExposureLog = {
      dishId: props.dishId,
      date: exposure_date.value,
      anxiety_level: anxiety_level.value,
      taste: taste.value,
      bodys_reaction: bodys_reaction.value,
      additional_notes: additional_notes.value,
    }

  loading.value = true;

  try {
    const response = await fetchWithAuth(
      `${apiUrl.value}/api/logs`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(new_log_data),
      },
      store
    );

    if (response.ok) {
      const response_json = await response.json();
      const logId = response_json['logId'];
      new_log_data.id = logId;
      emit('logAdded', new_log_data);
      resetForm();
      hideForm();
    } else {
      const errorData = await response.json().catch(() => ({}));
      alert(`Failed to add log: ${errorData.error || 'Unknown error'}`);
    }
    loading.value = false;
  } catch (error) {
    loading.value = false;
    console.error('An error occurred while adding the log:', error);
    alert('An unexpected error occurred. Please try again later.');
  }
};

const isHiding = ref(false);

watch(() => props.show, (newVal) => {
  if (!newVal) {
    isHiding.value = true;
    setTimeout(() => {
      isHiding.value = false;
    }, 300); // Match your transition time (0.3s)
  }
});
</script>

<style>
.add-dish-form-container {
  width: 100vw;
  height: 0;
  background-color: white;
  position: fixed;
  bottom: 0;
  left: 0;
  overflow: hidden;
  transition: height 0.3s ease;
  pointer-events: none;
  opacity: 0;
  overflow-y: scroll;
}

.add-dish-form-container.show {
  height: 100vh;
  opacity: 1;
  pointer-events: auto;
}

.add-dish-form-container.hiding {
  height: 0;
  opacity: 0;
  pointer-events: none;
}

#add-dish-form {
  width: 100%;
  max-width: 500px;
  padding: 25px 10px 0px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 auto;
}

#add-dish-form > div:nth-of-type(1) {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
}

#add-dish-form > div:nth-of-type(1) > p {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

#add-dish-form > div:nth-of-type(1) > button {
  background-color: transparent;
}

.slider-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.slider-value {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--primary-color);
}

#add-dish-form input[type='range'] {
  width: 100%;
}

#add-dish-form textarea {
  width: 100%;
  resize: none;
  min-height: 80px;
  padding: 13px;
}

#add-dish-form label {
  margin-top: 10px;
}

#close_form_button {
  padding: 10px;
  border-radius: 5px;
}

#close_form_button:hover {
  background-color: hsl(28, 100%, 96%) !important;
}

.taste-buttons {
  display: flex;
  gap: 10px;
}

.taste-buttons button {
  flex: 1;
  padding: 10px 0;
  border: 1px solid #ccc;
  background-color: white;
  color: black;
  cursor: pointer;
  border-radius: 5px;
  font-weight: 600;
  transition: background-color 0.2s, color 0.2s;
}

.taste-buttons button.selected {
  background-color: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.date-input-wrapper input[type='date'] {
  padding-right: 2em;
  width: 100%;
  box-sizing: border-box;
}
.calendar-icon {
  position: absolute;
  right: 0.5em;
  pointer-events: none;
  color: #aaa;
}
.input-hint {
  font-size: 0.8em;
  color: #888;
}
</style>
